<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  total: { type: Number, default: 0 },
  validRows: { type: Array, default: () => [] },
  selectedLabelIds: { type: Array, default: () => [] },
  newLabels: { type: Array, default: () => [] },
  importId: { type: [Number, String], default: null },
  importStatus: { type: String, default: 'pending' },
  processed: { type: Number, default: 0 },
  failedRecordsUrl: { type: String, default: null },
  errorMessage: { type: String, default: null },
  skippedDuplicates: { type: Number, default: 0 },
});

const store = useStore();
const allLabels = computed(() => store.getters['labels/getLabels'] || []);
const selectedLabels = computed(() =>
  allLabels.value.filter(l => props.selectedLabelIds.includes(l.id))
);

const sampleContacts = computed(() => props.validRows.slice(0, 30));
const progressPct = computed(() => {
  if (!props.total) return 0;
  return Math.min(100, Math.round((props.processed / props.total) * 100));
});

const statusLabel = computed(() => {
  switch (props.importStatus) {
    case 'pending':    return 'Aguardando início…';
    case 'processing': return `Processando ${props.processed}/${props.total}…`;
    case 'completed':  return `Concluído: ${props.processed} contatos importados.`;
    case 'failed':     return 'Falha na importação. Verifique o relatório.';
    default:           return props.importStatus;
  }
});
</script>

<template>
  <div class="sz-step-review">
    <h4>Resumo do envio</h4>
    <p class="sz-hint">
      Os {{ validRows.length }} contatos serão importados com as etiquetas vinculadas.
      <template v-if="skippedDuplicates > 0">
        ({{ skippedDuplicates }} já existem na base e serão ignorados)
      </template>
    </p>

    <div class="sz-summary-card">
      Contatos a importar:<br>
      <strong>{{ validRows.length }} contatos</strong>
      <small v-if="skippedDuplicates > 0">
        + {{ skippedDuplicates }} duplicados ignorados
      </small>
    </div>

    <div class="sz-review-cols">
      <section>
        <h5>Etiquetas</h5>
        <div class="sz-pill-list">
          <span v-if="!selectedLabels.length && !newLabels.length" class="sz-empty">Nenhuma etiqueta vinculada</span>
          <span
            v-for="lbl in selectedLabels" :key="`sel-${lbl.id}`"
            class="sz-pill"
          >
            <span class="sz-pill-dot" :style="{ background: lbl.color }" />
            <span :style="{ color: lbl.color }">{{ lbl.title }}</span>
          </span>
          <span
            v-for="(lbl, idx) in newLabels" :key="`new-${idx}`"
            class="sz-pill"
          >
            <span class="sz-pill-dot" :style="{ background: lbl.color }" />
            <span :style="{ color: lbl.color }">{{ lbl.title }}</span>
          </span>
        </div>
      </section>

      <section>
        <header class="sz-review-header">
          <h5>Contatos</h5>
          <span class="sz-counter">{{ validRows.length }} contatos</span>
        </header>
        <div class="sz-contact-list">
          <div v-for="r in sampleContacts" :key="r.line" class="sz-contact-item">
            <strong>{{ r.name }}</strong>
            <span>{{ r.phone_number }}</span>
            <div class="sz-contact-pills">
              <span v-for="lbl in selectedLabels" :key="lbl.id" class="sz-pill sz-pill-mini">
                <span class="sz-pill-dot" :style="{ background: lbl.color }" />
                <span :style="{ color: lbl.color }">{{ lbl.title }}</span>
              </span>
              <span v-for="(lbl, i) in newLabels" :key="`n-${i}`" class="sz-pill sz-pill-mini">
                <span class="sz-pill-dot" :style="{ background: lbl.color }" />
                <span :style="{ color: lbl.color }">{{ lbl.title }}</span>
              </span>
            </div>
          </div>
          <div v-if="validRows.length > sampleContacts.length" class="sz-contact-more">
            +{{ validRows.length - sampleContacts.length }} contatos
          </div>
        </div>
      </section>
    </div>

    <div v-if="importId" class="sz-progress-block" :class="{ 'sz-progress-failed': importStatus === 'failed' }">
      <div class="sz-progress-bar"><span :style="{ width: progressPct + '%' }" /></div>
      <p>{{ statusLabel }}</p>
      <p v-if="errorMessage" class="sz-progress-error-detail">
        <strong>Detalhes:</strong> {{ errorMessage }}
      </p>
      <a v-if="failedRecordsUrl" :href="failedRecordsUrl" download>
        Baixar relatório de falhas
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
h4 { margin: 0 0 6px; font-size: 14px; font-weight: 600; }
h5 { margin: 0 0 8px; font-size: 12px; font-weight: 500; }
.sz-hint { color: #71717a; font-size: 12px; margin: 0 0 16px; }

.sz-summary-card {
  background: rgba(0,0,0,0.04);
  border-radius: 10px; padding: 16px;
  font-size: 13px; margin-bottom: 16px;
}
.sz-summary-card strong { font-size: 16px; }
.sz-summary-card small { display: block; margin-top: 4px; color: #b45309; font-size: 11px; }


.sz-review-cols {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
}
.sz-review-header {
  display: flex; align-items: center; justify-content: space-between;
}
.sz-counter { font-size: 11px; color: #71717a; }

.sz-pill-list { display: flex; flex-wrap: wrap; gap: 6px; }
.sz-empty { color: #71717a; font-size: 12px; }
.sz-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  background: #fafafa; border: 1px solid rgba(0,0,0,0.06);
  font-size: 11px;
}

.sz-pill-mini { font-size: 10px; padding: 2px 8px; }
.sz-pill-dot { width: 7px; height: 7px; border-radius: 999px; }

.sz-contact-list {
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 10px; padding: 8px;
  max-height: 320px; overflow: auto;
}

.sz-contact-item {
  display: grid; grid-template-columns: 1fr; gap: 2px;
  padding: 8px 10px; border-radius: 8px;
}
.sz-contact-item:hover { background: rgba(0,0,0,0.03); }
.sz-contact-item strong { font-size: 13px; font-weight: 500; }
.sz-contact-item span { font-size: 12px; color: #71717a; }
.sz-contact-pills { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.sz-contact-more {
  text-align: center; padding: 8px; font-size: 12px; color: #71717a;
}

.sz-progress-block {
  margin-top: 18px; padding: 14px;
  background: rgba(22,163,74,0.06);
  border-radius: 10px;
}
.sz-progress-bar {
  width: 100%; height: 6px; border-radius: 999px;
  background: rgba(0,0,0,0.06); overflow: hidden;
}
.sz-progress-bar span {
  display: block; height: 100%;
  background: #16a34a; transition: width 0.3s;
}
.sz-progress-block p { margin: 8px 0 0; font-size: 12px; }
.sz-progress-block a { color: #dc2626; font-size: 12px; text-decoration: underline; }
.sz-progress-failed {
  background: rgba(220,38,38,0.06);
}
.sz-progress-failed .sz-progress-bar span { background: #dc2626; }
.sz-progress-error-detail {
  font-size: 11px;
  color: #b91c1c;
  background: rgba(220,38,38,0.1);
  padding: 8px 10px;
  border-radius: 6px;
  margin-top: 8px !important;
  word-break: break-word;
}

</style>
